import type { ReactNode } from 'react';
import React, { ComponentProps, FC } from 'react';
import { ChevronRightIcon } from '@components/icons/ChevronRight.icon';
import { ProTag } from '@components/ProTag/ProTag.component';
import { Text } from '@components/Text/Text.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { PlcColor } from '@styles/colors';
import { getSlugLinkProps, urlEnum } from '@utils/url';
import classnames from 'classnames';

interface Props extends Omit<ComponentProps<'article'>, 'title'> {
  title: string;
  header: ReactNode;
  href: urlEnum;
  slug: string;
  isPro?: boolean;
  isWithChevron?: boolean;
  isWithBorder?: boolean;
  index?: number;
}

export const ContentCardLight: FC<Props> = ({
  title,
  header,
  href,
  slug,
  isPro = false,
  isWithChevron,
  isWithBorder,
  index,
  className,
  ...rest
}) => {
  const linkProps = getSlugLinkProps(href, slug);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const titleVariant = isMobile ? 'h5' : 'h4';
  const rootClassName = classnames('content-card-light', className);

  return isWithChevron ? (
    <PlcAnchor linkProps={linkProps} niveau3={href === urlEnum.guides ? title : ''}>
      <article
        className={rootClassName}
        {...rest}
        style={
          isWithBorder
            ? {
                borderBottom: index && index >= 0 ? 'none' : `1px solid ${PlcColor.LIGHT_GREEN}`,
              }
            : undefined
        }
      >
        <div className="plc-mb-s flex align-center">
          {header}
          {isPro && <ProTag className="plc-ml-xs plc-mr-0" />}
        </div>
        <div className="flex align-center row plc-mb-m">
          <Text className="flex align-center" tag="div" variant={titleVariant}>
            {title}
          </Text>
          <div className="plc-px-xs plc-ml-auto">
            <ChevronRightIcon fillColor={PlcColor.GREEN} height={14} stroke={PlcColor.GREEN} width={14} />
          </div>
        </div>
      </article>
    </PlcAnchor>
  ) : (
    <PlcAnchor linkProps={linkProps} niveau3={href === urlEnum.guides ? title : ''}>
      <article className={rootClassName} {...rest}>
        <div className="plc-mb-s flex align-center">
          {header}
          {isPro && <ProTag className="plc-ml-xs plc-mr-0" />}
        </div>
        <Text className="plc-mb-s flex align-center" tag="div" variant={titleVariant}>
          {title}
        </Text>
      </article>
    </PlcAnchor>
  );
};
